import {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
  Dispatch,
} from 'react';
import { ContentEditableDiv } from '@/components/ContentEditableDiv';
import { ScreenElementBoundingBox } from './ScreenElementBoundingBox';
import {
  EventScreenAction,
  selectScreenElement,
  updateScreenElement,
} from '@/providers/sharedActions';
import { ScreenElementFragment } from '@/generated/graphql';
import { useActiveClassNamesAndStyles } from '@/hooks/useActiveClassNamesAndStyles';
import { SharedScreenState, EngagementMode } from '@/types/screen';
import { useGoogleFonts } from '@/hooks/useGoogleFonts';
import { shouldMountScreenElementBoundingBox } from './shouldMountScreenElementBoundingBox';
import { useSearchParams } from 'react-router-dom';

export function ScreenTextElement({
  elementId,
  editor,
  state,
  dispatch,
  className: otherClassName,
}: {
  elementId: string;
  editor: boolean;
  state: SharedScreenState;
  dispatch: Dispatch<EventScreenAction>;
  className?: string;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === 'true';
  const showBoundingBox = shouldMountScreenElementBoundingBox({
    editor,
    preview,
  });
  const [editing, setEditing] = useState(false);
  const [internalText, setInternalText] = useState('');
  const [disableAnimation, setDisableAnimation] = useState(false);

  const element =
    state.savedTheme.stageElements?.[elementId] ||
    state.savedTheme.fanElements?.[elementId];

  let hasActiveEngagement =
    state.engagementMode === EngagementMode.Guide ||
    Boolean(
      state.activeEngagement && state.engagementMode === EngagementMode.Actual,
    );

  if (!editor) {
    hasActiveEngagement = Boolean(state.activeEngagement);
  }

  const { className, styles: activeStyles } = useActiveClassNamesAndStyles(
    element || ({} as ScreenElementFragment),
    hasActiveEngagement,
  );

  const selected = state.selectedElementId === elementId;

  // Initialize internalText when element is loaded
  useEffect(() => {
    if (element && element.text && internalText === '') {
      setInternalText(element.text);
    }
  }, [element, internalText]);

  useEffect(() => {
    if (
      !editing &&
      element &&
      internalText !== element.text &&
      internalText !== ''
    ) {
      dispatch(
        updateScreenElement({ element: { ...element, text: internalText } }),
      );
    }
  }, [editing, internalText, element, dispatch]);

  useGoogleFonts({ fontFamily: element?.fontFamily });

  const handleClick = useCallback(() => {
    if (editor) {
      if (!selected) {
        dispatch(selectScreenElement({ id: elementId }));
      }
    } else if (element?.linkHref) {
      window.open(element.linkHref, '_blank');
    }
  }, [editor, selected, elementId, dispatch, element?.linkHref]);

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        setEditing(false);
      } else if (event.key === 'Escape') {
        setEditing(false);
      }
    },
    [setEditing],
  );

  const handleChange = useCallback(
    (event: React.FormEvent<HTMLDivElement>) =>
      setInternalText((event.target as HTMLDivElement).innerText),
    [setInternalText],
  );

  const onUpdate = useCallback(
    (element: ScreenElementFragment) =>
      dispatch(updateScreenElement({ element })),
    [dispatch],
  );

  const { wrapperStyles, contentStyles } = useMemo(() => {
    const { width, height, top, left, ...rest } = activeStyles;
    const wrapperStyles = { width, height, top, left };
    const contentStyles = rest;

    if (element?.textType === 'heading') {
      contentStyles.fontFamily = 'var(--screen-heading-font-family)';
    } else {
      contentStyles.fontFamily = 'var(--screen-body-font-family)';
    }

    // color
    contentStyles.color = element?.color || state.savedTheme.foregroundColor;

    return {
      wrapperStyles,
      contentStyles,
    };
  }, [
    activeStyles,
    element?.color,
    element?.textType,
    state.savedTheme.foregroundColor,
  ]);

  if (!element) {
    return null;
  }

  return (
    <div
      data-name="TEXT_STAGE_ELEMENT_WRAPPER"
      data-id={elementId}
      ref={wrapperRef}
      className={`absolute ${disableAnimation ? '' : 'transition-all duration-1000'} `}
      style={wrapperStyles}
    >
      <ContentEditableDiv
        editable={editing}
        data-name="TEXT_STAGE_ELEMENT"
        data-id={elementId}
        className={`flex h-full w-full flex-col ${className} ${otherClassName || ''} ${editing ? 'z-20' : ''} ${disableAnimation ? '' : 'transition-all duration-1000'} `}
        style={contentStyles}
        onClick={handleClick}
        text={editing ? internalText : element.text}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onBlur={() => setEditing(false)}
      />
      {showBoundingBox && (
        <ScreenElementBoundingBox
          selected={selected}
          onSelect={handleClick}
          ref={wrapperRef}
          element={element}
          activeEngagement={hasActiveEngagement}
          onUpdate={onUpdate}
          onDoubleClick={() => setEditing(true)}
          onDragStart={() => setDisableAnimation(true)}
          onDragEnd={() => setTimeout(() => setDisableAnimation(false), 0)}
        />
      )}
    </div>
  );
}
